import { useState, useEffect, useCallback } from 'react';
import { Download, Users, Mail, Lock, LogOut, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminSubscribersPage = () => {
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [password, setPassword] = useState('');
  const [logging, setLogging] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('subscribers');
  const [subs, setSubs] = useState({ total: 0, active: 0, subscribers: [] });
  const [members, setMembers] = useState({ total: 0, members: [] });

  const authHeaders = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLogging(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/admin/login`, { password });
      const t = res.data.token;
      setToken(t);
      sessionStorage.setItem('admin_token', t);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLogging(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    sessionStorage.removeItem('admin_token');
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [s, m] = await Promise.all([
          axios.get(`${API}/api/admin/subscribers`, authHeaders()),
          axios.get(`${API}/api/admin/members`, authHeaders()),
        ]);
        setSubs(s.data);
        setMembers(m.data);
      } catch {
        setToken('');
        sessionStorage.removeItem('admin_token');
      }
    })();
  }, [token, authHeaders]);

  const downloadCSV = (data, filename, fields) => {
    const header = fields.join(',');
    const rows = data.map(r => fields.map(f => `"${String(r[f] || '').replace(/"/g, '""')}"`).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6" data-testid="admin-login-page">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <Lock className="w-8 h-8 text-[#C0C0C0]/40 mx-auto mb-4" />
            <h1 className="text-2xl font-serif italic text-[#E8E8E8]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Admin Access
            </h1>
            <p className="text-xs text-gray-600 mt-2">Subscriber management portal</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-4">
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Admin password" required autoFocus
              className="w-full bg-[#111] border border-white/10 text-white px-4 py-3 text-sm placeholder-gray-600 focus:border-[#C0C0C0]/30 focus:outline-none"
              data-testid="admin-password-input" />
            {error && <p className="text-red-400 text-xs flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
            <Button type="submit" disabled={logging} data-testid="admin-login-btn"
              className="w-full bg-[#C0C0C0] text-[#0a0a0a] hover:bg-[#E8E8E8] py-3 text-[10px] uppercase tracking-[0.2em]">
              {logging ? 'Verifying...' : 'Sign In'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  const activeSubs = subs.subscribers.filter(s => !s.unsubscribed);

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-20 px-6 md:px-12 pb-16" data-testid="admin-subscribers-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-serif italic text-[#E8E8E8]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Subscriber Portal
            </h1>
            <p className="text-xs text-gray-600 mt-1">Manage subscribers and community members</p>
          </div>
          <button onClick={handleLogout} className="text-gray-500 hover:text-white text-xs uppercase tracking-wider flex items-center gap-2">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: 'Subscribers', value: subs.active || activeSubs.length, icon: Mail },
            { label: 'Unsubscribed', value: subs.total - (subs.active || activeSubs.length), icon: Mail },
            { label: 'Members', value: members.total, icon: Users },
            { label: 'Total Emails', value: (subs.active || activeSubs.length) + members.total, icon: Users },
          ].map(s => (
            <div key={s.label} className="bg-[#111] border border-white/5 p-5">
              <s.icon className="w-4 h-4 text-[#C0C0C0]/30 mb-2" />
              <p className="text-2xl font-serif text-[#E8E8E8]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{s.value}</p>
              <p className="text-[9px] uppercase tracking-[0.2em] text-gray-600 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-6 border-b border-white/5 mb-6">
          {['subscribers', 'members'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`pb-3 text-[10px] uppercase tracking-[0.2em] border-b-2 transition-colors ${
                tab === t ? 'border-[#C0C0C0] text-white' : 'border-transparent text-gray-600 hover:text-gray-400'
              }`}>
              {t === 'subscribers' ? `Newsletter (${activeSubs.length})` : `Members (${members.total})`}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'subscribers' && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => downloadCSV(activeSubs, 'subscribers.csv', ['email', 'source', 'subscribed_at'])}
                className="text-[10px] uppercase tracking-wider text-[#C0C0C0]/60 hover:text-[#C0C0C0] flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Download CSV
              </button>
            </div>
            <div className="bg-[#111] border border-white/5">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 text-[9px] uppercase tracking-[0.2em] text-gray-600">
                <div className="col-span-5">Email</div>
                <div className="col-span-2">Source</div>
                <div className="col-span-3">Date</div>
                <div className="col-span-2">Status</div>
              </div>
              {subs.subscribers.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 last:border-0 text-sm">
                  <div className="col-span-5 text-[#E8E8E8] truncate">{s.email}</div>
                  <div className="col-span-2 text-gray-500 text-xs">{s.source || 'footer'}</div>
                  <div className="col-span-3 text-gray-500 text-xs">{s.subscribed_at?.slice(0, 10)}</div>
                  <div className="col-span-2">
                    <span className={`text-[9px] uppercase tracking-wider ${s.unsubscribed ? 'text-red-400' : 'text-green-400'}`}>
                      {s.unsubscribed ? 'Unsubscribed' : 'Active'}
                    </span>
                  </div>
                </div>
              ))}
              {subs.subscribers.length === 0 && (
                <p className="text-gray-600 text-center py-8 text-sm">No subscribers yet</p>
              )}
            </div>
          </div>
        )}

        {tab === 'members' && (
          <div>
            <div className="flex justify-end mb-4">
              <button onClick={() => downloadCSV(members.members, 'members.csv', ['email', 'username', 'role', 'created_at'])}
                className="text-[10px] uppercase tracking-wider text-[#C0C0C0]/60 hover:text-[#C0C0C0] flex items-center gap-1.5">
                <Download className="w-3.5 h-3.5" /> Download CSV
              </button>
            </div>
            <div className="bg-[#111] border border-white/5">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 text-[9px] uppercase tracking-[0.2em] text-gray-600">
                <div className="col-span-4">Email</div>
                <div className="col-span-3">Username</div>
                <div className="col-span-3">Role</div>
                <div className="col-span-2">Joined</div>
              </div>
              {members.members.map((m, i) => (
                <div key={i} className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/5 last:border-0 text-sm">
                  <div className="col-span-4 text-[#E8E8E8] truncate">{m.email}</div>
                  <div className="col-span-3 text-gray-500">{m.username}</div>
                  <div className="col-span-3 text-[#C0C0C0]/60 text-xs">{m.role}</div>
                  <div className="col-span-2 text-gray-500 text-xs">{m.created_at?.slice(0, 10) || '—'}</div>
                </div>
              ))}
              {members.members.length === 0 && (
                <p className="text-gray-600 text-center py-8 text-sm">No members yet</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSubscribersPage;
